/**
 * Busca inteligente com tolerância a acentos, erros de digitação e busca por palavras.
 * 
 * Funcionalidades:
 * - Remove acentos (varão = varao)
 * - Busca por cada palavra digitada separadamente
 * - Match parcial no início de palavras
 * - Tolerância a 1-2 caracteres errados (fuzzy)
 */

// Remove acentos e normaliza texto
const removerAcentos = (str) =>
    str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

// Normaliza: lowercase + sem acentos + trim
const normalizar = (str) =>
    removerAcentos((str || '').toLowerCase().trim());

/**
 * Calcula distância de Levenshtein entre duas strings (limitada para performance).
 * Retorna um número — quanto menor, mais parecidas as strings são.
 */
const levenshtein = (a, b) => {
    if (a === b) return 0;
    if (a.length === 0) return b.length;
    if (b.length === 0) return a.length;

    // Limitar para strings curtas (performance)
    if (a.length > 20 || b.length > 20) return Math.abs(a.length - b.length);

    const matrix = [];
    for (let i = 0; i <= b.length; i++) matrix[i] = [i];
    for (let j = 0; j <= a.length; j++) matrix[0][j] = j;

    for (let i = 1; i <= b.length; i++) {
        for (let j = 1; j <= a.length; j++) {
            if (b[i - 1] === a[j - 1]) {
                matrix[i][j] = matrix[i - 1][j - 1];
            } else {
                matrix[i][j] = Math.min(
                    matrix[i - 1][j - 1] + 1, // substituição
                    matrix[i][j - 1] + 1,       // inserção
                    matrix[i - 1][j] + 1         // remoção
                );
            }
        }
    }
    return matrix[b.length][a.length];
};

/**
 * Verifica se um termo de busca "bate" com um texto alvo.
 * - Exato/parcial: includes simples
 * - Início de palavra: "var" → "varão"  
 * - Fuzzy: até 1 erro para termos curtos, até 2 para termos >= 5 chars
 */
const termoCorresponde = (textoNorm, termoNorm) => {
    if (!termoNorm) return true;
    if (!textoNorm) return false;

    // Match direto (includes)
    if (textoNorm.includes(termoNorm)) return true;

    // Match por início de palavra
    const palavrasTexto = textoNorm.split(/\s+/);
    for (const palavra of palavrasTexto) {
        if (palavra.startsWith(termoNorm)) return true;
    }

    // Fuzzy: comparar termo com cada palavra do texto
    const tolerancia = termoNorm.length >= 5 ? 2 : 1;
    // Só aplica fuzzy se o termo tiver pelo menos 3 caracteres
    if (termoNorm.length >= 3) {
        for (const palavra of palavrasTexto) {
            // Comparar com substring do mesmo tamanho para palavras longas
            if (palavra.length >= termoNorm.length) {
                const sub = palavra.substring(0, termoNorm.length);
                if (levenshtein(sub, termoNorm) <= tolerancia) return true;
            }
            // Comparar palavra inteira se for curta
            if (levenshtein(palavra, termoNorm) <= tolerancia) return true;
        }
    }

    return false;
};

// Stop words - preposições/artigos comuns que são ignorados na busca
const STOP_WORDS = new Set([
    'de', 'do', 'da', 'dos', 'das',
    'para', 'pra', 'pro',
    'com', 'sem', 'em', 'no', 'na', 'nos', 'nas',
    'um', 'uma', 'uns', 'umas',
    'o', 'a', 'os', 'as',
    'e', 'ou', 'que',
    'por', 'pelo', 'pela',
    'ao', 'aos',
]);

/**
 * Função principal de busca inteligente.
 * 
 * @param {string} busca - Texto digitado pelo usuário
 * @param {string[]} campos - Array de valores dos campos a buscar
 * @returns {boolean} - true se o item deve aparecer no resultado
 * 
 * Exemplo:
 *   buscaInteligente("varao para cortina 2m", [produto.nome, produto.cor, produto.sku])
 */
export const buscaInteligente = (busca, campos) => {
    if (!busca || !busca.trim()) return true;

    const todosTermos = normalizar(busca).split(/\s+/).filter(Boolean);
    
    // Remove stop words, mas mantém se for o único termo digitado
    const termos = todosTermos.length > 1
        ? todosTermos.filter(t => !STOP_WORDS.has(t))
        : todosTermos;
    
    // Se sobrou vazio após filtrar stop words, usa os termos originais
    const termosFinal = termos.length > 0 ? termos : todosTermos;
    
    // Concatena todos os campos em um texto único normalizado
    const textoCompleto = campos
        .filter(Boolean)
        .map(normalizar)
        .join(' ');

    // Cada termo digitado deve corresponder a pelo menos algo no texto
    return termosFinal.every(termo => termoCorresponde(textoCompleto, termo));
};

export default buscaInteligente;
